import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Clock, BookOpen, Eye, Upload, Brain, FileText, UserCircle } from 'lucide-react';
import CourseCard from '../../components/NewCourseCard';

// Main Dashboard Component
function MyClasses() {
  const [selectedClass, setSelectedClass] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('unit-planning');

  const [classes] = useState([
    {
      id: 1,
      name: 'Mathematics - Grade 7',
      role: 'teacher',
      schedule: 'Mon, Wed, Fri 9:00 AM',
      students: 28,
      room: 'A-204',
    },
    {
      id: 2,
      name: 'Mathematics - Grade 8',
      role: 'teacher',
      schedule: 'Mon, Wed, Fri 11:00 AM',
      students: 26,
      room: 'A-204',
    },
    {
      id: 3,
      name: 'Mathematics - Grade 7 (Section B)',
      role: 'teacher',
      schedule: 'Tue, Thu 9:00 AM',
      students: 30,
      room: 'A-204',
    },
    {
      id: 4,
      name: 'Algebra - Grade 9',
      role: 'teaching_assistant',
      schedule: 'Tue, Thu 2:00 PM',
      students: 32,
      room: 'B-105',
    },
    {
      id: 5,
      name: 'Geometry - Grade 10',
      role: 'teaching_assistant',
      schedule: 'Wed 1:00 PM',
      students: 24,
      room: 'B-108',
    },
    {
      id: 6,
      name: 'Mathematics - Grade 6',
      role: 'substitute',
      schedule: 'Fri 10:00 AM',
      students: 25,
      room: 'A-101',
    },
    {
      id: 7,
      name: 'Advanced Calculus - Grade 12',
      role: 'observer',
      schedule: 'Thu 3:00 PM',
      students: 18,
      room: 'A-305',
    },
  ]);

  // Sample course data
  const sampleCourses = [
    {
      _id: '1',
      title: 'Introduction to Algebra',
      description: 'Basic algebraic concepts and operations',
      thumbnail: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400',
    },
    {
      _id: '2',
      title: 'Linear Equations',
      description: 'Solving and graphing linear equations',
      thumbnail: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=400',
    },
    {
      _id: '3',
      title: 'Geometry Fundamentals',
      description: 'Shapes, angles, and geometric properties',
      thumbnail: 'https://images.unsplash.com/photo-1596495577886-d920f1fb7238?w=400',
    },
  ];

  // Sample students data
  const sampleStudents = [
    { id: 1, name: 'Alice Johnson', email: 'alice.j@school.edu', attendance: '95%', grade: 'A' },
    { id: 2, name: 'Bob Smith', email: 'bob.s@school.edu', attendance: '88%', grade: 'B+' },
    { id: 3, name: 'Charlie Brown', email: 'charlie.b@school.edu', attendance: '92%', grade: 'A-' },
    { id: 4, name: 'Diana Prince', email: 'diana.p@school.edu', attendance: '98%', grade: 'A+' },
    { id: 5, name: 'Ethan Hunt', email: 'ethan.h@school.edu', attendance: '85%', grade: 'B' },
  ];

  const getRoleBadge = (role) => {
    const roleConfig = {
      teacher: { label: 'Teacher', className: 'bg-blue-500 hover:bg-blue-600' },
      teaching_assistant: { label: 'Teaching Assistant', className: 'bg-green-500 hover:bg-green-600 text-white' },
      substitute: { label: 'Substitute', className: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' },
      observer: { label: 'Observer', className: 'bg-purple-500 hover:bg-purple-600 text-white border-purple-500' },
    };

    const config = roleConfig[role] || roleConfig.teacher;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'observer':
        return <Eye className="w-5 h-5 text-purple-500" />;
      case 'teaching_assistant':
        return <Users className="w-5 h-5 text-green-500" />;
      case 'substitute':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <BookOpen className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleClassClick = (classItem) => {
    setSelectedClass(classItem);
    setSidebarTab('unit-planning');
  };

  const handleBackToDashboard = () => {
    setSelectedClass(null);
  };

  // Render class detail page
  if (selectedClass) {
    return (
      <div className="flex h-screen bg-slate-50">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
          <div className="p-6 border-b border-slate-200">
            <Button variant="ghost" onClick={handleBackToDashboard} className="mb-4 -ml-2">
              ← Back to Classes
            </Button>
            <h2 className="font-bold text-lg text-slate-800">{selectedClass.name}</h2>
            <p className="text-sm text-slate-600 mt-1">{selectedClass.room}</p>
          </div>

          <nav className="flex-1 p-4">
            <button
              onClick={() => setSidebarTab('unit-planning')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                sidebarTab === 'unit-planning'
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}>
              <BookOpen className="w-5 h-5" />
              Unit Planning
            </button>

            <button
              onClick={() => setSidebarTab('class-drive')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                sidebarTab === 'class-drive'
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}>
              <FileText className="w-5 h-5" />
              Class Drive
            </button>

            <button
              onClick={() => setSidebarTab('students')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                sidebarTab === 'students' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700 hover:bg-slate-100'
              }`}>
              <UserCircle className="w-5 h-5" />
              Students
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-8">
            {sidebarTab === 'unit-planning' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-slate-800 mb-2">Unit Planning</h1>
                  <p className="text-slate-600">All courses in this unit</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sampleCourses.map((course) => (
                    <div key={course._id}>
                      <CourseCard course={course} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarTab === 'class-drive' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-slate-800 mb-2">Class Drive</h1>
                  <p className="text-slate-600">Upload and manage learning materials</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-dashed border-slate-300 hover:border-blue-400">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Upload className="w-12 h-12 text-slate-400 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Upload Materials</h3>
                      <p className="text-sm text-slate-500 text-center">
                        Upload documents, videos, and other learning resources
                      </p>
                      <Button className="mt-4">Choose Files</Button>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-blue-200 bg-blue-50">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Brain className="w-12 h-12 text-blue-600 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Train AI Assistant</h3>
                      <p className="text-sm text-slate-500 text-center">
                        Train AI with uploaded materials for personalized help
                      </p>
                      <Button className="mt-4 bg-blue-600 hover:bg-blue-700">Start Training</Button>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">Uploaded Materials</h2>
                  <div className="bg-white rounded-lg border border-slate-200">
                    <div className="p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Name</span>
                        <span className="text-sm font-medium text-slate-700">Date</span>
                      </div>
                    </div>
                    <div className="p-8 text-center text-slate-500">No materials uploaded yet</div>
                  </div>
                </div>
              </div>
            )}

            {sidebarTab === 'students' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-slate-800 mb-2">Students</h1>
                  <p className="text-slate-600">{selectedClass.students} students enrolled</p>
                </div>

                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Name</th>
                            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Email</th>
                            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Attendance</th>
                            <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Grade</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {sampleStudents.map((student) => (
                            <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                              <td className="px-6 py-4 text-sm text-slate-800 font-medium">{student.name}</td>
                              <td className="px-6 py-4 text-sm text-slate-600">{student.email}</td>
                              <td className="px-6 py-4 text-sm text-slate-600">{student.attendance}</td>
                              <td className="px-6 py-4">
                                <Badge variant="outline" className="font-semibold">
                                  {student.grade}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render main dashboard
  const groupedClasses = {
    teacher: classes.filter((c) => c.role === 'teacher'),
    teaching_assistant: classes.filter((c) => c.role === 'teaching_assistant'),
    substitute: classes.filter((c) => c.role === 'substitute'),
    observer: classes.filter((c) => c.role === 'observer'),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">My Classes</h1>
          <p className="text-slate-600">Overview of all your assigned classes and roles</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Teaching</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.teacher.length}</p>
                </div>
                <BookOpen className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Assisting</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.teaching_assistant.length}</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Substitute</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.substitute.length}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Observing</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.observer.length}</p>
                </div>
                <Eye className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classItem) => (
            <Card
              key={classItem.id}
              className="hover:shadow-lg transition-shadow duration-200 cursor-pointer"
              onClick={() => handleClassClick(classItem)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getRoleIcon(classItem.role)}
                    <CardTitle className="text-lg">{classItem.name}</CardTitle>
                  </div>
                </div>
                <div className="flex items-center gap-2">{getRoleBadge(classItem.role)}</div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-slate-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{classItem.schedule}</span>
                  </div>
                  <div className="flex items-center text-slate-600">
                    <Users className="w-4 h-4 mr-2" />
                    <span>{classItem.students} students</span>
                  </div>
                  <div className="flex items-center text-slate-600">
                    <BookOpen className="w-4 h-4 mr-2" />
                    <span>Room {classItem.room}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MyClasses;
